import { type ReactNode, useEffect, useMemo, useState } from 'react'
import {
  AlertCircle,
  CheckCircle2,
  ChevronRight,
  Clock3,
  ExternalLink,
  FileCode2,
  FileText,
  FolderOpen,
  GitBranch,
  Info,
  Network,
  Play,
  Router,
  SquareTerminal,
  XCircle,
} from 'lucide-react'
import { useNavigate, useParams } from 'react-router-dom'
import { ViolationCard, type NormalizedViolation } from '@/components/invariant-details'
import { ValidationProgress } from '@/components/validation-progress'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { useConfirm } from '@/components/ui/use-confirm'
import { useToast } from '@/components/ui/use-toast'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/lib/api-client'
import type { Job, Validation } from '@/lib/api-client'
import type { Network as NetworkType } from '@/types/network'
import { cn } from '@/lib/utils'

type ValidationSummary = {
  total: number
  passed: number
  failed: number
  warnings: number
  errors: number
}

type ValidationInvariant = {
  id: string
  name: string
  type: string
  status: string
  description?: string
  message?: string
  details?: Record<string, unknown>
}

const STATUS_TONE: Record<string, string> = {
  success: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  passed: 'border-emerald-200 bg-emerald-50 text-emerald-700',
  failure: 'border-red-200 bg-red-50 text-red-700',
  failed: 'border-red-200 bg-red-50 text-red-700',
  error: 'border-red-200 bg-red-50 text-red-700',
  warning: 'border-amber-200 bg-amber-50 text-amber-700',
  cancelled: 'border-border bg-slate-100 text-muted-foreground',
  running: 'border-amber-200 bg-amber-50 text-amber-700',
  pending: 'border-border bg-slate-100 text-muted-foreground',
  queued: 'border-border bg-slate-100 text-muted-foreground',
}

const INVARIANT_ROW_TONE: Record<string, { border: string; surface: string; hover: string; icon: string }> = {
  success: {
    border: 'border-l-emerald-500',
    surface: 'bg-emerald-50/80',
    hover: 'hover:bg-emerald-50',
    icon: 'text-emerald-700',
  },
  passed: {
    border: 'border-l-emerald-500',
    surface: 'bg-emerald-50/80',
    hover: 'hover:bg-emerald-50',
    icon: 'text-emerald-700',
  },
  failure: {
    border: 'border-l-red-600',
    surface: 'bg-red-50/80',
    hover: 'hover:bg-red-50',
    icon: 'text-red-700',
  },
  failed: {
    border: 'border-l-red-600',
    surface: 'bg-red-50/80',
    hover: 'hover:bg-red-50',
    icon: 'text-red-700',
  },
  error: {
    border: 'border-l-red-600',
    surface: 'bg-red-50/80',
    hover: 'hover:bg-red-50',
    icon: 'text-red-700',
  },
  warning: {
    border: 'border-l-amber-500',
    surface: 'bg-amber-50/80',
    hover: 'hover:bg-amber-50',
    icon: 'text-amber-700',
  },
  running: {
    border: 'border-l-amber-500',
    surface: 'bg-amber-50/80',
    hover: 'hover:bg-amber-50',
    icon: 'text-amber-700',
  },
  pending: {
    border: 'border-l-slate-400',
    surface: 'bg-muted/90',
    hover: 'hover:bg-muted',
    icon: 'text-muted-foreground',
  },
  queued: {
    border: 'border-l-slate-400',
    surface: 'bg-muted/90',
    hover: 'hover:bg-muted',
    icon: 'text-muted-foreground',
  },
}

const formatDate = (value?: string | null) => {
  if (!value) {
    return ''
  }
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return ''
  }
  return date.toLocaleString()
}

const formatValidationType = (type?: string) => {
  if (type === 'analysis') return 'Analysis'
  if (type === 'simulation') return 'Simulation'
  return 'Validation'
}

const formatShortId = (value?: string | null) => {
  if (!value) {
    return 'Unknown'
  }
  return value.slice(-8)
}

const getDateMs = (value?: string | null) => {
  if (!value) {
    return null
  }
  const date = new Date(value)
  const time = date.getTime()
  if (Number.isNaN(time)) {
    return null
  }
  return time
}

const hasValidJobId = (jobId?: string | null) => {
  if (!jobId) {
    return false
  }
  const trimmed = jobId.trim()
  return Boolean(trimmed) && trimmed.toLowerCase() !== 'invalid'
}

const getResultSummary = (validation: Validation): ValidationSummary | null => {
  const summary = validation.results?.summary as Partial<ValidationSummary> | undefined
  if (!summary) {
    return null
  }

  return {
    total: Number(summary.total ?? 0),
    passed: Number(summary.passed ?? 0),
    failed: Number(summary.failed ?? 0),
    warnings: Number(summary.warnings ?? 0),
    errors: Number(summary.errors ?? 0),
  }
}

const getInvariantList = (validation: Validation): ValidationInvariant[] => {
  const resultsData = validation.results as { invariants?: { data?: unknown } | unknown } | undefined
  const invariantsData = (resultsData?.invariants as { data?: unknown })?.data || resultsData?.invariants

  if (!invariantsData) {
    return []
  }

  const invariantValues = Array.isArray(invariantsData) ? invariantsData : Object.values(invariantsData)
  return invariantValues
    .filter((invariant): invariant is Record<string, unknown> => Boolean(invariant && typeof invariant === 'object'))
    .map((invariant, index) => ({
      id: typeof invariant.id === 'string' ? invariant.id : `invariant-${index + 1}`,
      name: typeof invariant.name === 'string' && invariant.name ? invariant.name : `Invariant ${index + 1}`,
      type: typeof invariant.type === 'string' ? invariant.type : 'unknown',
      status: typeof invariant.status === 'string' ? invariant.status : 'unknown',
      description: typeof invariant.description === 'string' ? invariant.description : undefined,
      message: typeof invariant.message === 'string' ? invariant.message : undefined,
      details: invariant.details && typeof invariant.details === 'object'
        ? invariant.details as Record<string, unknown>
        : undefined,
    }))
}

const getBranchName = (validation: Validation, job: Job | null) => {
  const resultBranch = (validation.results?.details as { branch?: string } | undefined)?.branch
  if (typeof resultBranch === 'string' && resultBranch) {
    return resultBranch
  }
  if (job?.branch) {
    return job.branch
  }
  return 'main'
}

const getDurationLabel = (durationSeconds: number | null) => {
  if (durationSeconds === null) {
    return 'In progress'
  }
  if (durationSeconds < 60) {
    return `${durationSeconds}s`
  }
  const minutes = Math.floor(durationSeconds / 60)
  const seconds = durationSeconds % 60
  return `${minutes}m ${seconds}s`
}

const getOutcome = (validation: Validation) => {
  if (validation.status === 'completed' && validation.result === 'success') {
    return {
      label: 'Passed',
      tone: 'success',
      badgeTone: STATUS_TONE.success,
      icon: CheckCircle2,
      heading: 'Validation passed',
      summary: 'All invariants passed.',
    }
  }
  if (validation.status === 'completed' && validation.result === 'failure') {
    return {
      label: 'Failed',
      tone: 'danger',
      badgeTone: STATUS_TONE.failure,
      icon: AlertCircle,
      heading: 'Validation failed',
      summary: 'Review failed invariants.',
    }
  }
  if (validation.status === 'cancelled') {
    return {
      label: 'Cancelled',
      tone: 'default',
      badgeTone: STATUS_TONE.cancelled,
      icon: XCircle,
      heading: 'Validation cancelled',
      summary: 'Execution stopped before completion.',
    }
  }
  if (validation.status === 'running' || validation.status === 'pending' || validation.status === 'queued') {
    return {
      label: validation.status === 'queued' ? 'Queued' : validation.status === 'pending' ? 'Pending' : 'Running',
      tone: 'warning',
      badgeTone: STATUS_TONE.running,
      icon: Clock3,
      heading: 'Validation in progress',
      summary: 'Execution is still in progress.',
    }
  }

  return {
    label: validation.status,
    tone: 'default',
    badgeTone: STATUS_TONE[validation.status] ?? STATUS_TONE.cancelled,
    icon: Info,
    heading: 'Validation detail',
    summary: 'Review this validation.',
  }
}

const getInvariantIcon = (type: string) => {
  switch (type) {
    case 'reachability':
      return <Network className="h-4 w-4" />
    case 'bgp_sessions':
    case 'bgp_session_status':
      return <Router className="h-4 w-4" />
    default:
      return <Info className="h-4 w-4" />
  }
}

const getInvariantRowTone = (status?: string) => {
  const normalized = status?.toLowerCase() ?? 'unknown'
  return INVARIANT_ROW_TONE[normalized] ?? {
    border: 'border-l-slate-300',
    surface: 'bg-background',
    hover: 'hover:bg-muted',
    icon: 'text-muted-foreground',
  }
}

const getInvariantStatusBadge = (status?: string, className?: string) => {
  const normalized = status?.toLowerCase() ?? 'unknown'
  return (
    <Badge variant="outline" className={cn('uppercase', STATUS_TONE[normalized], className)}>
      {normalized}
    </Badge>
  )
}

const getRawLogs = (validation: Validation) => {
  if (typeof validation.logs === 'string' && validation.logs.trim()) {
    return validation.logs
  }

  const resultLogs = validation.results?.logs
  if (typeof resultLogs === 'string' && resultLogs.trim()) {
    return resultLogs
  }
  if (Array.isArray(resultLogs)) {
    return resultLogs.map((entry) => String(entry)).join('\n')
  }
  return ''
}

function RailSection({
  title,
  children,
}: {
  title: string
  children: ReactNode
}) {
  return (
    <section className="overflow-hidden rounded-lg border border-border bg-background">
      <div className="border-b border-border/70 px-4 py-3">
        <h2 className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">{title}</h2>
      </div>
      <div className="space-y-4 px-4 py-4">{children}</div>
    </section>
  )
}

function MetadataRow({
  label,
  value,
}: {
  label: string
  value: string
}) {
  return (
    <div className="space-y-1.5">
      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="break-words text-sm font-medium text-foreground">{value}</p>
    </div>
  )
}

export default function ValidationPage() {
  const params = useParams()
  const navigate = useNavigate()
  const { isAuthenticated } = useAuth()
  const { confirm } = useConfirm()
  const { toast } = useToast()
  const [validation, setValidation] = useState<Validation | null>(null)
  const [job, setJob] = useState<Job | null>(null)
  const [network, setNetwork] = useState<NetworkType | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [cancelError, setCancelError] = useState<string | null>(null)
  const [isCancelling, setIsCancelling] = useState(false)
  const [selectedInvariantId, setSelectedInvariantId] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState('evidence')

  // Live progress tracking from SSE events
  type LiveInvariant = { index: number; name: string; type: string; status: 'pending' | 'running' | 'completed' | 'failed' }
  const [liveInvariants, setLiveInvariants] = useState<LiveInvariant[]>([])

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login')
      return
    }
    if (params.id) {
      void fetchValidation()
    }
  }, [isAuthenticated, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!validation || !['running', 'pending', 'queued'].includes(validation.status)) {
      return
    }

    const interval = window.setInterval(() => {
      void fetchValidation()
    }, 3000)
    return () => window.clearInterval(interval)
  }, [validation?.status, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  // SSE connection for live invariant progress
  useEffect(() => {
    if (!validation || !['running', 'pending', 'queued'].includes(validation.status) || !params.id) {
      return
    }

    const token = localStorage.getItem('access_token')
    if (!token) return

    const apiUrl = window.location.origin
    const url = `${apiUrl}/api/v1/validations/${params.id}/progress?token=${token}`
    const eventSource = new EventSource(url)
    const processedIds = new Set<string>()

    eventSource.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data)
        if (parsed.type !== 'event' || !parsed.event) return
        const evt = parsed.event
        if (processedIds.has(evt.id)) return
        processedIds.add(evt.id)

        const step = evt.data?.step
        const metadata = evt.data?.metadata

        if (step === 'question_started' && metadata) {
          const idx = metadata.question_index ?? 0
          const total = metadata.total_questions ?? 0
          const name = metadata.invariant_name || metadata.question_type || `Question ${idx + 1}`
          const type = metadata.question_type || ''

          setLiveInvariants(prev => {
            // Initialize full list on first event if needed
            if (prev.length === 0 && total > 0) {
              const initial: LiveInvariant[] = Array.from({ length: total }, (_, i) => ({
                index: i, name: `Question ${i + 1}`, type: '', status: 'pending' as const,
              }))
              initial[idx] = { index: idx, name, type, status: 'running' }
              return initial
            }
            const next = [...prev]
            if (idx < next.length) {
              next[idx] = { ...next[idx], name, type, status: 'running' }
            } else {
              next.push({ index: idx, name, type, status: 'running' })
            }
            return next
          })
        }

        if (step === 'question_done' && metadata) {
          const idx = metadata.question_index ?? 0
          const name = metadata.invariant_name || metadata.question_type || `Question ${idx + 1}`
          const type = metadata.question_type || ''
          const status = (metadata.status === 'completed' || metadata.status === 'success') ? 'completed' as const : 'failed' as const

          setLiveInvariants(prev => {
            const next = [...prev]
            if (idx < next.length) {
              next[idx] = { ...next[idx], name, type, status }
            }
            return next
          })
        }

        // Terminal event — stop SSE
        if (evt.type === 'validation.result') {
          eventSource.close()
        }
      } catch {
        // Ignore parse errors
      }
    }

    eventSource.onerror = () => {
      // Reconnection is handled by EventSource natively
    }

    return () => {
      eventSource.close()
    }
  }, [validation?.status, params.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchValidation = async () => {
    try {
      setError(null)
      setCancelError(null)
      const response = await apiClient.getValidation(params.id!)

      if (!response.success || !response.data) {
        setError(response.error || 'Failed to load validation')
        if (response.error?.includes('404') || response.error?.includes('not found')) {
          navigate('/networks')
        }
        return
      }

      const nextValidation = response.data.validation
      setValidation(nextValidation)

      const jobId = nextValidation.jobId
      if (!hasValidJobId(jobId)) {
        setJob(null)
        return
      }

      try {
        const jobResponse = await apiClient.getJob(jobId)
        if (jobResponse.success && jobResponse.data) {
          const fetchedJob = (jobResponse.data as { job: Job }).job
          setJob(fetchedJob)

          // Fetch network for source config (GitHub links, etc.)
          if (fetchedJob.networkId) {
            try {
              const netResp = await apiClient.getNetwork(fetchedJob.networkId)
              if (netResp.success && netResp.data) {
                setNetwork(netResp.data.network)
              }
            } catch {
              // Non-fatal — network info is supplementary
            }
          }
        } else {
          setJob(null)
        }
      } catch {
        setJob(null)
      }
    } catch {
      setError('Failed to load validation')
    } finally {
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!validation) {
      return
    }

    const confirmed = await confirm({
      title: 'Cancel validation',
      description: 'This stops the current validation run and leaves existing results unchanged.',
      confirmLabel: 'Cancel validation',
      cancelLabel: 'Keep running',
      variant: 'destructive',
    })

    if (!confirmed) {
      return
    }

    setIsCancelling(true)
    setCancelError(null)
    try {
      const response = await apiClient.cancelValidation(validation.id)
      if (!response.success) {
        const message = response.error || 'Failed to cancel validation'
        setCancelError(message)
        toast({
          title: 'Cancel failed',
          description: message,
          variant: 'destructive',
        })
        return
      }

      setValidation((current) => current ? { ...current, status: 'cancelled' } : current)
      toast({
        title: 'Validation cancelled',
        description: 'The validation run has been stopped.',
        variant: 'success',
      })
    } catch (cancelFailure) {
      const message = cancelFailure instanceof Error ? cancelFailure.message : 'Failed to cancel validation'
      setCancelError(message)
      toast({
        title: 'Cancel failed',
        description: message,
        variant: 'destructive',
      })
    } finally {
      setIsCancelling(false)
    }
  }

  const invariants = useMemo(() => (validation ? getInvariantList(validation) : []), [validation])

  if (loading) {
    return (
      <div className="rounded-lg border border-border bg-background px-6 py-16 text-center">
        <div className="mx-auto inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-primary" />
        <p className="mt-3 text-sm text-muted-foreground">Loading validation...</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-background px-6 py-8">
        <div className="flex items-center gap-3 text-red-700">
          <XCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      </div>
    )
  }

  if (!validation) {
    return null
  }

  const startedAtText = formatDate(validation.startedAt)
  const completedAtText = formatDate(validation.completedAt)
  const startedAtMs = getDateMs(validation.startedAt)
  const completedAtMs = getDateMs(validation.completedAt)
  const durationSeconds = startedAtMs !== null && completedAtMs !== null
    ? Math.max(0, Math.round((completedAtMs - startedAtMs) / 1000))
    : null
  const jobId = hasValidJobId(validation.jobId) ? validation.jobId : ''
  const canCancel = ['running', 'pending', 'queued'].includes(validation.status)
  const summary = getResultSummary(validation)
  const outcome = getOutcome(validation)
  const OutcomeIcon = outcome.icon
  const invariantCount = summary?.total ?? invariants.length
  const failedCount = (summary?.failed ?? 0) + (summary?.errors ?? 0)
  const selectedInvariant = invariants.find((invariant) => invariant.id === selectedInvariantId) ?? null
  const logs = getRawLogs(validation)
  const primaryActionLabel = jobId ? 'Open Job' : 'Open Validations'
  const headerContext = job?.networkName
    ? `${job.networkName} • ${formatValidationType(validation.type)}`
    : formatValidationType(validation.type)

  return (
    <div className="space-y-6">
      <section className="flex flex-col gap-4 border-b border-border pb-6 lg:flex-row lg:items-end lg:justify-between">
        <div className="min-w-0 space-y-2">
          <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
            {job?.networkId ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/networks/${job.networkId}`)}
                  className="transition-colors hover:text-foreground"
                >
                  {job.networkName || 'Network'}
                </button>
                <ChevronRight className="h-4 w-4" />
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => navigate('/validations')}
                  className="transition-colors hover:text-foreground"
                >
                  Validations
                </button>
                <ChevronRight className="h-4 w-4" />
              </>
            )}
            {jobId ? (
              <>
                <button
                  type="button"
                  onClick={() => navigate(`/jobs/${jobId}`)}
                  className="transition-colors hover:text-foreground"
                >
                  Job #{formatShortId(jobId)}
                </button>
                <ChevronRight className="h-4 w-4" />
              </>
            ) : null}
            <span className="font-semibold text-foreground">Validation #{formatShortId(validation.id)}</span>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-[2rem] font-bold tracking-[-0.04em] text-foreground">
              Validation #{formatShortId(validation.id)}
            </h1>
            <Badge variant="outline" className={cn('uppercase', outcome.badgeTone)}>
              {outcome.label}
            </Badge>
          </div>

          <p className="text-sm text-muted-foreground">
            <span className="font-semibold text-foreground">{headerContext}</span>
            {startedAtText ? <span>{` • Started ${startedAtText}`}</span> : null}
            {job?.source ? <span>{` • Source ${job.source}`}</span> : null}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => navigate(jobId ? `/jobs/${jobId}` : '/validations')}>
            {primaryActionLabel}
          </Button>
          {job?.networkId ? (
            <Button variant="outline" onClick={() => navigate(`/networks/${job.networkId}`)}>
              Open Network
            </Button>
          ) : null}
          {job?.networkId ? (
            <Button onClick={() => navigate(`/networks/${job.networkId}`)}>
              <Play className="mr-2 h-4 w-4" />
              Run another Validation
            </Button>
          ) : null}
          {canCancel ? (
            <Button variant="destructive" onClick={handleCancel} disabled={isCancelling}>
              {isCancelling ? 'Cancelling...' : 'Cancel Validation'}
            </Button>
          ) : null}
        </div>
      </section>

      <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_20rem]">
        <div className="min-w-0 space-y-5">
          <section className="rounded-lg border border-border bg-background">
            <div className="space-y-4 px-5 py-5">
              <div className="flex items-start gap-4">
                <div className={cn(
                  'flex h-10 w-10 shrink-0 items-center justify-center rounded-md border',
                  outcome.badgeTone,
                )}>
                  <OutcomeIcon className="h-5 w-5" />
                </div>
                <div className="min-w-0 space-y-2">
                  <h2 className="text-2xl font-semibold tracking-tight text-foreground">{outcome.heading}</h2>
                  <p className="max-w-3xl text-sm leading-6 text-muted-foreground">
                    {outcome.summary}
                  </p>
                </div>
              </div>

              {cancelError ? (
                <Alert variant="destructive">
                  <AlertCircle className="h-4 w-4" />
                  <AlertTitle>Cancel failed</AlertTitle>
                  <AlertDescription>{cancelError}</AlertDescription>
                </Alert>
              ) : null}
            </div>
          </section>

          {(() => {
            const livePassed = liveInvariants.filter(i => i.status === 'completed').length
            const liveFailed = liveInvariants.filter(i => i.status === 'failed').length
            const liveTotal = liveInvariants.length
            const cards = summary ? [
              { label: 'Total', value: invariantCount, color: '' },
              { label: 'Passed', value: summary.passed, color: 'text-emerald-700' },
              { label: 'Failed', value: failedCount, color: failedCount > 0 ? 'text-red-600' : '' },
              { label: 'Warnings', value: summary.warnings, color: summary.warnings > 0 ? 'text-amber-700' : '' },
            ] : liveTotal > 0 ? [
              { label: 'Total', value: liveTotal, color: '' },
              { label: 'Passed', value: livePassed, color: 'text-emerald-700' },
              { label: 'Failed', value: liveFailed, color: liveFailed > 0 ? 'text-red-600' : '' },
              { label: 'Checking', value: liveTotal - livePassed - liveFailed, color: 'text-amber-700' },
            ] : null
            if (!cards) return null
            return (
              <section className="grid gap-3 sm:grid-cols-4">
                {cards.map((card) => (
                  <div key={card.label} className="rounded-lg border border-border bg-background px-4 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{card.label}</p>
                    <p className={cn('mt-1 text-2xl font-bold tabular-nums', card.color)}>{card.value}</p>
                  </div>
                ))}
              </section>
            )
          })()}

          <section className="overflow-hidden rounded-lg border border-border bg-background">
            <div className="flex items-center justify-between gap-4 border-b border-border/70 px-5 py-4">
              <div>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-muted-foreground">Invariant checks</p>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                <span>{summary?.passed ?? 0} passed</span>
                <span>•</span>
                <span>{failedCount} failed</span>
                <span>•</span>
                <span>{summary?.warnings ?? 0} warnings</span>
              </div>
            </div>

            {invariants.length === 0 && liveInvariants.length > 0 ? (
              <div className="divide-y divide-border/70">
                {liveInvariants.map((inv) => {
                  const isRunning = inv.status === 'running'
                  const isPassed = inv.status === 'completed'
                  const isFailed = inv.status === 'failed'
                  const isPending = inv.status === 'pending'
                  const StatusIcon = isFailed ? AlertCircle : isPassed ? CheckCircle2 : isRunning ? Clock3 : Clock3
                  const iconColor = isFailed ? 'text-red-600' : isPassed ? 'text-emerald-600' : isRunning ? 'text-amber-600' : 'text-muted-foreground/40'
                  const borderColor = isFailed ? 'border-l-red-600' : isPassed ? 'border-l-emerald-500' : isRunning ? 'border-l-amber-500' : 'border-l-transparent'
                  const bgColor = isRunning ? 'bg-amber-50/50 dark:bg-amber-950/20' : ''

                  return (
                    <div key={inv.index} className={cn('flex items-center gap-3 border-l-[3px] px-5 py-3.5', borderColor, bgColor)}>
                      <StatusIcon className={cn('h-4 w-4 shrink-0', iconColor, isRunning && 'animate-spin')} />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-foreground">{inv.name}</p>
                        {inv.type && inv.type !== inv.name && (
                          <p className="text-xs text-muted-foreground">{inv.type}</p>
                        )}
                      </div>
                      <span className={cn(
                        'text-[10px] font-semibold uppercase tracking-wider',
                        isFailed ? 'text-red-600' : isPassed ? 'text-emerald-600' : isRunning ? 'text-amber-600' : 'text-muted-foreground/50',
                      )}>
                        {isPending ? 'Pending' : isRunning ? 'Checking' : isPassed ? 'Passed' : 'Failed'}
                      </span>
                    </div>
                  )
                })}
              </div>
            ) : invariants.length === 0 ? (
              <div className="px-5 py-10 text-sm text-muted-foreground">
                Structured invariant results are not available yet for this validation.
              </div>
            ) : (
              <div className="divide-y divide-[#e5e9f0]">
                {(() => {
                  const CONFIG_CHECK_TYPES = new Set(['best_practices', 'best-practices', 'configuration', 'configuration_text'])
                  const analysisInvariants = invariants.filter((inv) => !CONFIG_CHECK_TYPES.has(inv.type))
                  const configInvariants = invariants.filter((inv) => CONFIG_CHECK_TYPES.has(inv.type))
                  const renderInvariantRow = (invariant: ValidationInvariant) => {
                    const selected = invariant.id === selectedInvariant?.id
                    const rowTone = getInvariantRowTone(invariant.status)
                    const invariantFindings = typeof invariant.details?.findings === 'string' ? invariant.details.findings : ''
                    const invariantViolations = Array.isArray(invariant.details?.violations)
                      ? invariant.details.violations as NormalizedViolation[]
                      : []

                    return (
                      <article key={invariant.id} className="bg-background">
                        <button
                          type="button"
                          onClick={() => setSelectedInvariantId((current) => current === invariant.id ? null : invariant.id)}
                          className={cn(
                            'flex w-full items-start gap-3 border-l-[3px] px-5 py-4 text-left transition-colors',
                            rowTone.border,
                            rowTone.surface,
                            selected ? 'ring-1 ring-inset ring-border' : rowTone.hover,
                          )}
                        >
                          <div className={cn('mt-0.5 shrink-0', rowTone.icon)}>{getInvariantIcon(invariant.type)}</div>
                          <div className="min-w-0 flex-1 space-y-1">
                            <div className="flex items-start justify-between gap-3">
                              <p className="break-words text-sm font-semibold text-foreground">{invariant.name}</p>
                              {selected && failedCount > 0 && (invariant.status === 'failed' || invariant.status === 'error') ? (
                                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-red-600">
                                  Action required
                                </span>
                              ) : null}
                              {!selected ? getInvariantStatusBadge(invariant.status, 'shrink-0 text-[10px] font-semibold') : null}
                            </div>
                            <p className="break-words text-xs leading-5 text-muted-foreground">
                              {invariant.message || invariant.description || 'Select to review.'}
                            </p>
                          </div>
                        </button>

                      {selected ? (
                        <div className="space-y-5 border-t border-border/70 px-5 py-5">
                          <div className="flex flex-wrap items-center gap-2">
                            {getInvariantStatusBadge(invariant.status)}
                            <Badge variant="outline" className="border-border bg-muted text-muted-foreground">
                              {invariant.type}
                            </Badge>
                          </div>

                          {invariant.message ? (
                            <Alert className={invariant.status === 'failed' ? 'border-red-200' : undefined}>
                              <Info className="h-4 w-4" />
                              <AlertTitle>Invariant summary</AlertTitle>
                              <AlertDescription>{invariant.message}</AlertDescription>
                            </Alert>
                          ) : null}

                          {invariant.description ? (
                            <div className="space-y-2">
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Intent</p>
                              <p className="text-sm leading-6 text-muted-foreground">{invariant.description}</p>
                            </div>
                          ) : null}

                          {invariantFindings ? (
                            <div className="space-y-2">
                              <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Findings</p>
                              <p className="text-sm leading-6 text-muted-foreground">{invariantFindings}</p>
                            </div>
                          ) : null}

                          <ValidationProgress
                            validationId={validation.id}
                            validationStatus={validation.status}
                            selectedInvariant={invariant}
                            embedded
                          />

                          {invariantViolations.length > 0 ? (
                            <div className="space-y-3">
                              <p className="text-sm font-medium text-foreground">Violations ({invariantViolations.length})</p>
                              <div className="space-y-3">
                                {invariantViolations.map((violation, index) => (
                                  <ViolationCard key={`${invariant.id}-violation-${index}`} violation={violation} />
                                ))}
                              </div>
                            </div>
                          ) : null}

                          {invariant.details ? (
                            <details className="rounded-md border border-dashed border-border px-4 py-3">
                              <summary className="cursor-pointer text-sm font-medium text-muted-foreground hover:text-foreground">
                                View raw invariant details
                              </summary>
                              <pre className="mt-3 overflow-x-auto rounded-md bg-muted p-3 text-xs text-muted-foreground">
                                {JSON.stringify(invariant.details, null, 2)}
                              </pre>
                            </details>
                          ) : null}
                        </div>
                      ) : null}
                    </article>
                  )
                  }
                  return (
                    <>
                      {analysisInvariants.length > 0 && (
                        <>
                          <div className="bg-muted/40 px-5 py-2 border-b border-[#e5e9f0]">
                            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Analysis</p>
                          </div>
                          {analysisInvariants.map(renderInvariantRow)}
                        </>
                      )}
                      {configInvariants.length > 0 && (
                        <>
                          <div className="bg-muted/40 px-5 py-2 border-b border-[#e5e9f0]">
                            <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">Configuration Checks</p>
                          </div>
                          {configInvariants.map(renderInvariantRow)}
                        </>
                      )}
                    </>
                  )
                })()}
              </div>
            )}
          </section>
        </div>

        <div className="min-w-0 space-y-4">
          <RailSection title="Validation metadata">
            <MetadataRow label="Validation ID" value={validation.id} />
            <MetadataRow label="Job ID" value={validation.jobId || 'Unavailable'} />
            <MetadataRow label="Network" value={job?.networkName || 'Unavailable'} />
            <MetadataRow label="Source" value={job?.source || 'Unavailable'} />
            <MetadataRow label="Branch" value={getBranchName(validation, job)} />
            <MetadataRow label="Type" value={formatValidationType(validation.type)} />
            <MetadataRow label="Started" value={startedAtText || 'Not started'} />
            <MetadataRow label="Completed" value={completedAtText || 'In progress'} />
            <MetadataRow label="Duration" value={getDurationLabel(durationSeconds)} />
          </RailSection>

          {network?.sourceType === 'github' && network.sourceConfig && (() => {
            const sc = network.sourceConfig as { fullName?: string; owner?: string; name?: string }
            const repoFullName = sc.fullName || (sc.owner && sc.name ? `${sc.owner}/${sc.name}` : null)
            if (!repoFullName) return null
            const commitSha = job?.commitSHA
            const branch = getBranchName(validation, job)
            const baseUrl = `https://github.com/${repoFullName}`
            const treeRef = commitSha || branch

            return (
              <RailSection title="Source">
                <div className="space-y-3">
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Repository</p>
                    <a href={baseUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline">
                      {repoFullName} <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                  <div className="flex gap-4">
                    {commitSha && (
                      <div className="space-y-1.5">
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Commit</p>
                        <a href={`${baseUrl}/commit/${commitSha}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 font-mono text-sm text-primary hover:underline">
                          {commitSha.slice(0, 8)} <ExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                    <div className="space-y-1.5">
                      <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Branch</p>
                      <span className="inline-flex items-center gap-1.5 text-sm text-foreground">
                        <GitBranch className="h-3 w-3 text-muted-foreground" /> {branch}
                      </span>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Validation Policy</p>
                    <div className="space-y-1">
                      <a href={`${baseUrl}/blob/${treeRef}/.etherpilot/network.yml`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                        <FileCode2 className="h-3 w-3" /> network.yml <ExternalLink className="h-3 w-3" />
                      </a>
                      <a href={`${baseUrl}/blob/${treeRef}/.etherpilot/invariants.yml`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                        <FileText className="h-3 w-3" /> invariants.yml <ExternalLink className="h-3 w-3" />
                      </a>
                    </div>
                  </div>
                  <div className="space-y-1.5">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">Device Configs</p>
                    <a href={`${baseUrl}/tree/${treeRef}/configs`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm text-primary hover:underline">
                      <FolderOpen className="h-3 w-3" /> configs/ <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              </RailSection>
            )
          })()}

          <RailSection title="Validation evidence">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
              <TabsList className="grid h-auto grid-cols-3 bg-transparent p-0">
                <TabsTrigger
                  value="evidence"
                  className="rounded-md border border-border px-2 py-2 text-xs data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                >
                  <SquareTerminal className="mr-1.5 h-3.5 w-3.5" />
                  Evidence
                </TabsTrigger>
                <TabsTrigger
                  value="artifacts"
                  className="rounded-md border border-border px-2 py-2 text-xs data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                >
                  <FileCode2 className="mr-1.5 h-3.5 w-3.5" />
                  Artifacts
                </TabsTrigger>
                <TabsTrigger
                  value="metrics"
                  className="rounded-md border border-border px-2 py-2 text-xs data-[state=active]:border-primary data-[state=active]:bg-primary/10"
                >
                  <FileText className="mr-1.5 h-3.5 w-3.5" />
                  Metrics
                </TabsTrigger>
              </TabsList>

              <TabsContent value="evidence" className="mt-0 space-y-4">
                {validation.results?.errorMessage ? (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertTitle>Execution error</AlertTitle>
                    <AlertDescription>{validation.results.errorMessage}</AlertDescription>
                  </Alert>
                ) : null}

                <div className="space-y-2">
                  <div>
                    <p className="text-sm font-medium text-foreground">Recent logs</p>
                    <p className="text-xs text-muted-foreground">{`logs_run_${formatShortId(jobId || validation.id)}.stdout`}</p>
                  </div>
                  <pre className="max-h-[22rem] overflow-auto rounded-md bg-foreground p-4 text-xs leading-6 text-primary-foreground">
                    {logs || 'No logs are available for this Validation.'}
                  </pre>
                </div>
              </TabsContent>

              <TabsContent value="artifacts" className="mt-0 space-y-3">
                <div>
                  <p className="text-sm font-medium text-foreground">Structured results payload</p>
                  <p className="mt-1 text-xs leading-5 text-muted-foreground">Full validation output.</p>
                </div>
                <pre className="max-h-[22rem] overflow-auto rounded-md bg-muted p-4 text-xs text-muted-foreground">
                  {JSON.stringify(validation.results ?? {}, null, 2)}
                </pre>
              </TabsContent>

              <TabsContent value="metrics" className="mt-0 space-y-3">
                {[
                  ['Total Invariants', String(invariantCount)],
                  ['Passed', String(summary?.passed ?? 0)],
                  ['Failed', String(failedCount)],
                  ['Warnings', String(summary?.warnings ?? 0)],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-md border border-border px-3 py-3">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                    <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
                  </div>
                ))}
              </TabsContent>
            </Tabs>
          </RailSection>
        </div>
      </div>
    </div>
  )
}
